import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { getMovieBySlug, getRelatedMovies, Movie } from '@/mocks/movies';

const categoryNameMap: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  anime: '动漫',
  variety: '综艺',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [movie, setMovie] = useState<Movie | undefined>(undefined);
  const [related, setRelated] = useState<Movie[]>([]);

  useEffect(() => {
    if (slug) {
      const found = getMovieBySlug(slug);
      setMovie(found);
      if (found) {
        setRelated(getRelatedMovies(found, 6));
      }
    }
  }, [slug]);

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500 text-lg">影片不存在或已下架</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20 bg-background-50">
        <Breadcrumb
          items={[
            {
              label: categoryNameMap[movie.category] || movie.category,
              href: `/category/${movie.category}`,
            },
            { label: movie.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[300px] flex-shrink-0">
                <div className="sticky top-24">
                  <div className="rounded-xl overflow-hidden">
                    <img
                      src={movie.imageUrl}
                      alt={`${movie.title} 海报 - 80s电影网`}
                      title={`${movie.title} 高清在线看免费不卡`}
                      className="w-full aspect-[2/3] object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 font-bold mb-1">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-5">
                  {movie.title} 高清在线看免费不卡 | 蓝光画质极速播
                </p>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                    {movie.subcategory}
                  </span>
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                    {movie.year}
                  </span>
                  <span className="flex items-center gap-1 px-3 py-1 bg-accent-100/70 text-accent-800 text-xs font-bold rounded-full">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    {movie.rating}
                  </span>
                  {movie.isNew && (
                    <span className="px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full">
                      最新上线
                    </span>
                  )}
                  {movie.isHot && (
                    <span className="px-3 py-1 bg-accent-500 text-foreground-950 text-xs font-medium rounded-full">
                      热门推荐
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8 p-5 bg-background-100 rounded-xl">
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">导演</p>
                    <p className="text-sm font-medium text-foreground-950">{movie.director}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">主演</p>
                    <p className="text-sm font-medium text-foreground-950 line-clamp-1">
                      {movie.actors.join('、')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">时长/集数</p>
                    <p className="text-sm font-medium text-foreground-950">{movie.duration}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">类型</p>
                    <p className="text-sm font-medium text-foreground-950">{movie.subcategory}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">年份</p>
                    <p className="text-sm font-medium text-foreground-950">{movie.year}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">更新状态</p>
                    <p className="text-sm font-medium text-primary-600">{movie.updateInfo}</p>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="font-heading text-lg text-foreground-950 font-bold mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {movie.description}
                  </p>
                </div>

                {movie.category === 'tv' || movie.category === 'anime' || movie.category === 'variety' ? (
                  <div className="mb-8">
                    <h2 className="font-heading text-lg text-foreground-950 font-bold mb-3">剧集列表</h2>
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                      {Array.from({ length: 12 }, (_, i) => (
                        <button
                          key={i}
                          className="h-10 bg-background-100 hover:bg-primary-100 text-sm text-foreground-700 hover:text-primary-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                        >
                          第{i + 1}集
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mb-8">
                    <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-base font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-play-fill text-xl"></i>
                      </div>
                      立即播放
                    </button>
                  </div>
                )}
              </div>
            </div>

            {related.length > 0 && (
              <section className="mt-14">
                <div className="flex items-end justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl text-foreground-950 font-bold mb-1">
                      猜你喜欢
                    </h2>
                    <p className="text-sm text-foreground-500">
                      更多{categoryNameMap[movie.category] || '精彩'}推荐
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {related.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: movie.imageUrl,
            dateCreated: `${movie.year}`,
            director: {
              '@type': 'Person',
              name: movie.director,
            },
            actor: movie.actors.map((a) => ({
              '@type': 'Person',
              name: a,
            })),
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: movie.rating,
              bestRating: '10',
              worstRating: '0',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.80sdyw.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: categoryNameMap[movie.category] || movie.category,
                item: `https://www.80sdyw.com/category/${movie.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: movie.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}